import styled, { createGlobalStyle } from 'styled-components'
import { Modal, ModalContent } from 'suomifi-ui-components'
import { IconClose } from 'suomifi-icons'
import { useModaaliBackdropSulku } from '../utils/modaaliBackdropSulku'

/**
 * Laajennettu versio sticky-muistiinpanosta — sama sisältö modaalissa
 * suuremmalla kirjoituspinnalla. Sama otsikko, väliotsikko ja tekstilaatikko
 * kuin sivupalkkikomponentissa, ei tallenna-painiketta. Autosaves.
 */

interface Props {
  visible: boolean
  onClose: () => void
  teksti: string
  onChange: (uusi: string) => void
}

const MODAL_MAX_WIDTH = '760px'

/* DS:n Modal levennetään 760px:iin — pidempi muistiinpano näkyy
   kerralla ilman scrollausta. Modal kokonaan keltaiseksi: poistetaan
   .fi-modal_content -padding ja annetaan YellowContainerin täyttää
   koko modaalin. Modaalin oma reuna saa myös keltaisen taustan jotta
   yhden pikselin border-light:t eivät paista valkoisina. */
const ModalWidthOverride = createGlobalStyle`
  .sticky-modal-root.sticky-modal-root.fi-modal {
    width: ${MODAL_MAX_WIDTH} !important;
    max-width: calc(100% - 2rem) !important;
    background: hsl(48, 100%, 86%) !important;
  }
  .sticky-modal-root.sticky-modal-root .fi-modal_content {
    padding: 0 !important;
  }
`

const YellowContainer = styled.div`
  position: relative;
  background: hsl(48, 100%, 86%);
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.l};
  min-height: 32rem;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.insetXs};
`

const Title = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.brandBase};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

const Hint = styled.span`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`

/* Sulkupainike absoluuttisesti modaalin oikeaan yläkulmaan — irti
   sisältövirrasta, sama paikka kuin natiiveissa modaaleissa. */
const CloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.s};
  right: ${({ theme }) => theme.spacing.s};
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.xxs};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.depthDark1};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;

  &:hover {
    background: hsla(48, 100%, 50%, 0.15);
    color: ${({ theme }) => theme.colors.brandBase};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 1px;
  }

  & > svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const Textarea = styled.textarea`
  border: 0;
  outline: 0;
  background: transparent;
  resize: none;
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.insetM};
  font: ${({ theme }) => theme.typography.bodyText};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.blackBase};
  flex: 1;
  font-family: inherit;
  line-height: 1.5;

  &::placeholder {
    color: ${({ theme }) => theme.colors.depthDark1};
  }
`

export default function MuistiinpanoStickyModal({
  visible,
  onClose,
  teksti,
  onChange,
}: Props) {
  useModaaliBackdropSulku(visible, onClose)
  return (
    <>
      <ModalWidthOverride />
      <Modal
        appElementId="root"
        visible={visible}
        onEscKeyDown={onClose}
        scrollable={false}
        className="sticky-modal-root"
      >
        <ModalContent>
          <YellowContainer>
            <CloseButton
              type="button"
              onClick={onClose}
              aria-label="Sulje"
              title="Sulje"
            >
              <IconClose aria-hidden />
            </CloseButton>
            <TitleBlock>
              <Title>Sisäiset muistiinpanot</Title>
              <Hint>Näkyy vain virkailijoille.</Hint>
            </TitleBlock>
            <Textarea
              value={teksti}
              onChange={e => onChange(e.target.value)}
              placeholder="Kirjoita muistiinpano..."
              aria-label="Sisäiset muistiinpanot"
              autoFocus
            />
          </YellowContainer>
        </ModalContent>
      </Modal>
    </>
  )
}
